import React, { useEffect, useMemo, useState } from "react";
import { Download, BookOpen, Search } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

export default function Doctrine() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/doctrine").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const t = useMemo(
    () => ({
      eyebrow: isAr ? "المراجع الفقهية" : "Legal Doctrine",
      title: isAr
        ? "كتب الفقه القانوني — مراجع علمية."
        : "Foundational legal-theory and doctrinal treatises.",
      lead: isAr
        ? "مرجعية فقهية مقارنة: كتب لكبار فقهاء القانون الجنائي والمدني ونظرية القانون. تُستخدم للمفاهيم والأصول النظرية فقط — وليست قانوناً سارياً في دولة الكويت."
        : "Scholarly reference works in criminal law, civil law, and legal theory. Used for concepts and theoretical foundations only — not binding statutes in the State of Kuwait.",
      search: isAr ? "ابحث بعنوان الكتاب أو المؤلف..." : "Search by title or author…",
      empty: isAr ? "لم تُضَف مراجع فقهية بعد." : "No doctrinal references added yet.",
      download: isAr ? "تحميل الكتاب" : "Download Book",
      by: isAr ? "تأليف" : "By",
      ocrPending: isAr ? "جارٍ استخراج النص…" : "Text extraction in progress…",
      pages: (n) => {
        const v = Number(n) || 0;
        return isAr ? `${v.toLocaleString("ar-KW")} حرف` : `${v.toLocaleString()} chars`;
      },
    }),
    [isAr],
  );

  const filtered = items.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.title || "").toLowerCase().includes(q) ||
      (r.author || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="doctrine-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-7">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <div className="mb-8 max-w-md relative">
        <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          dir="auto"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          data-testid="doctrine-search"
          className="rounded-sm h-11 ps-9"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500">
          {t.empty}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          {filtered.map((r) => {
            const isReady = (r.char_count || 0) > 0;
            return (
              <article
                key={r.id}
                data-testid={`doctrine-item-${r.id}`}
                className="bg-white p-8 lg:p-10 flex flex-col"
              >
                <div className="flex items-start gap-3 mb-4">
                  <BookOpen className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                  <p className="eyebrow">
                    {r.language === "ar" ? "AR" : "EN"} ·{" "}
                    {new Date(r.uploaded_at).toLocaleDateString(
                      isAr ? "ar-KW" : "en-GB",
                    )}
                  </p>
                </div>
                <h2 className="font-display text-2xl lg:text-3xl leading-tight mb-3">
                  {r.title}
                </h2>
                {r.author && (
                  <p className="text-sm text-slate-500 mb-3">
                    {t.by}: {r.author}
                  </p>
                )}
                {isReady ? (
                  <p className="text-xs uppercase tracking-[0.18em] text-emerald-700 mb-4">
                    {t.pages(r.char_count)}
                  </p>
                ) : (
                  <p className="text-xs uppercase tracking-[0.18em] text-amber-700 mb-4 inline-flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    {t.ocrPending}
                  </p>
                )}
                {r.summary && (
                  <p className="text-slate-700 leading-relaxed mb-6 flex-1">
                    {r.summary}
                  </p>
                )}
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/doctrine/${r.id}/download`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`doctrine-download-${r.id}`}
                  className="inline-flex items-center gap-2 self-start px-4 h-10 text-xs uppercase tracking-[0.18em] border border-slate-300 hover:border-slate-900 transition-colors mt-auto"
                >
                  <Download className="w-3.5 h-3.5" /> {t.download}
                </a>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
